import { basisParam, getBasis } from "@app/lib/basis";
import { getPerspectiveId } from "@app/lib/perspective";
import PnlMonthPage, { DEFAULT_THRESHOLD, pnlMonthQuery } from "@app/pages/pnl-month/pnl-month-page";
import type { pnlMonthPage_Query } from "@app/queries/pnlMonthPage_Query.graphql";
import { environment } from "@app/relay/relay-environment";
import { pageHead } from "@app/lib/page-title";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadQuery } from "react-relay";

type PnlMonthSearch = { threshold?: number };

export const Route = createFileRoute("/_authed/pnl_/$month")({
  // The counterparty-visibility threshold lives in the URL so a drilled month is
  // shareable exactly as it was read. The month itself is page-owned (exempt from
  // the global date lens), like /pnl's year navigator.
  validateSearch: (search: Record<string, unknown>): PnlMonthSearch => ({
    threshold: typeof search.threshold === "number" ? search.threshold : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ params, deps }) =>
    loadQuery<pnlMonthPage_Query>(
      environment,
      pnlMonthQuery,
      {
        month: params.month,
        perspectiveId: getPerspectiveId() ?? undefined,
        threshold: deps.threshold ?? DEFAULT_THRESHOLD,
        basis: basisParam(getBasis()) ?? undefined,
      },
      { fetchPolicy: "network-only" }
    ),
  head: ({ params }) => pageHead(`P&L ${params.month}`),
  component: PnlMonthRoute,
});

function PnlMonthRoute() {
  const queryRef = Route.useLoaderData();

  return (
    <ErrorBoundary fallback={<div className="p-6 text-red-500">Error loading the month statement</div>}>
      <Suspense fallback={<div className="p-6 text-gray-500">Building the month statement...</div>}>
        <PnlMonthPage queryRef={queryRef} />
      </Suspense>
    </ErrorBoundary>
  );
}
